import React from "react";
import CommonSelect from "../../../../core/common/commonSelect";
import ImageSelect from "../../../../core/common/imageSelect";
import PredefinedDateRanges from "../../../../core/common/datePicker";
import type { ImageOption, Option } from "../types";

interface TodoFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  activeFiltersCount: number;
  /** The view's temp filter object; only the fields read below are required. */
  tempFilters: {
    selectedTag: string;
    selectedStatus: string;
    selectedRecurring: string;
    createDateFilter: { start: string; end: string } | null;
    dueDateFilter: { start: string; end: string } | null;
  };
  setTempFilters: (updater: (prev: any) => any) => void;

  tagOptions: Option[];
  statusOptions: Option[];
  recurringOptions: Option[];

  reporterOptions: ImageOption[];
  followerOptions: ImageOption[];
  assigneeOptions: ImageOption[];

  onTagChange: (value: string) => void;
  onReporterChange: (value: string) => void;
  onFollowerChange: (value: string) => void;
  onAssigneeChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onRecurringChange: (value: string) => void;

  onApply: () => void;
  onReset: () => void;

  /** Bumping this remounts the ImageSelects (used to clear them on reset). */
  selectKey?: number;
}

/**
 * The shared off-canvas filter panel for both Task views. Date / select state is
 * held by the parent view via `tempFilters`; the panel is otherwise stateless.
 */
const TodoFilterPanel: React.FC<TodoFilterPanelProps> = ({
  isOpen,
  onClose,
  activeFiltersCount,
  tempFilters,
  setTempFilters,
  tagOptions,
  statusOptions,
  recurringOptions,
  reporterOptions,
  followerOptions,
  assigneeOptions,
  onTagChange,
  onReporterChange,
  onFollowerChange,
  onAssigneeChange,
  onStatusChange,
  onRecurringChange,
  onApply,
  onReset,
  selectKey,
}) => {
  return (
    <div
      className={`offcanvas offcanvas-end ${isOpen ? "show" : ""}`}
      id="filterPanel"
      style={{ maxWidth: "50%", width: "100%" }}
      aria-labelledby="filterPanelLabel"
    >
      <div className="offcanvas-header">
        <h5 className="offcanvas-title" id="filterPanelLabel">
          Filters
          {activeFiltersCount > 0 && (
            <span className="badge bg-primary rounded-pill ms-2">
              {activeFiltersCount}
            </span>
          )}
        </h5>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="offcanvas"
          onClick={onClose}
        ></button>
      </div>
      <div className="offcanvas-body" style={{ padding: "1.5rem" }}>
        <div className="row g-3">
          <div className="col-md-6 col-12">
            <label className="form-label">Create Date</label>
            <div className="me-3">
              <div className="input-icon-end position-relative">
                <PredefinedDateRanges
                  onDateChange={(start: any, end: any) => {
                    setTempFilters((prev) => ({
                      ...prev,
                      createDateFilter: {
                        start: start.format("YYYY-MM-DD"),
                        end: end.format("YYYY-MM-DD"),
                      },
                    }));
                  }}
                />
                <span className="input-icon-addon">
                  <i className="ti ti-chevron-down" />
                </span>
              </div>
            </div>
            {tempFilters.createDateFilter && (
              <button
                className="btn btn-sm btn-light mt-2"
                onClick={() =>
                  setTempFilters((prev) => ({ ...prev, createDateFilter: null }))
                }
              >
                Clear Filter
              </button>
            )}
          </div>

          <div className="col-md-6 col-12">
            <label className="form-label">Due Date</label>
            <div className="me-3">
              <div className="input-icon-end position-relative">
                <PredefinedDateRanges
                  onDateChange={(start: any, end: any) => {
                    setTempFilters((prev) => ({
                      ...prev,
                      dueDateFilter: {
                        start: start.format("YYYY-MM-DD"),
                        end: end.format("YYYY-MM-DD"),
                      },
                    }));
                  }}
                />
                <span className="input-icon-addon">
                  <i className="ti ti-chevron-down" />
                </span>
              </div>
            </div>
            {tempFilters.dueDateFilter && (
              <button
                className="btn btn-sm btn-light mt-2"
                onClick={() =>
                  setTempFilters((prev) => ({ ...prev, dueDateFilter: null }))
                }
              >
                Clear Filter
              </button>
            )}
          </div>

          <div className="col-md-6 col-12">
            <label className="form-label">Tag</label>
            <CommonSelect
              className="select"
              options={tagOptions}
              defaultValue={tagOptions.find(
                (tag) => tag.value === tempFilters.selectedTag,
              )}
              onChange={(selected: any) => onTagChange(selected.value)}
            />
          </div>

          <div className="col-md-6 col-12">
            <label className="form-label">Assigned By</label>
            <ImageSelect
              key={selectKey}
              options={reporterOptions}
              onChange={(selected: any) => onReporterChange(selected.value)}
              placeHolder="Select member"
            />
          </div>

          <div className="col-md-6 col-12">
            <label className="form-label">Followers</label>
            <ImageSelect
              key={selectKey}
              options={followerOptions}
              onChange={(selected: any) => onFollowerChange(selected.value)}
              placeHolder="Select member"
            />
          </div>

          <div className="col-md-6 col-12">
            <label className="form-label">Assignee</label>
            <ImageSelect
              key={selectKey}
              options={assigneeOptions}
              onChange={(selected: any) => onAssigneeChange(selected.value)}
              placeHolder="Select member"
            />
          </div>

          <div className="col-md-6 col-12">
            <label className="form-label">Stages</label>
            <CommonSelect
              className="select"
              options={statusOptions}
              defaultValue={statusOptions.find(
                (status) => status.value === tempFilters.selectedStatus,
              )}
              onChange={(selected: any) => onStatusChange(selected.value)}
            />
          </div>

          <div className="col-md-6 col-12">
            <label className="form-label">Recurring</label>
            <CommonSelect
              className="select"
              options={recurringOptions}
              defaultValue={recurringOptions.find(
                (recurring) => recurring.value === tempFilters.selectedRecurring,
              )}
              onChange={(selected: any) => onRecurringChange(selected.value)}
            />
          </div>
        </div>

        <div className="d-flex justify-content-between mt-4">
          <button
            className="btn btn-secondary"
            data-bs-dismiss="offcanvas"
            onClick={onReset}
          >
            Reset
          </button>
          <button
            className="btn btn-primary"
            data-bs-dismiss="offcanvas"
            onClick={onApply}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default TodoFilterPanel;
